import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import React, { useCallback, useEffect, useState } from "react";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import Image from "next/image";
import { SegmentInfo, TotalPriceInfo } from "@/types/search/flight";
import FlightLine from "./flight-line";
import { useFlightSearch } from "@/hooks/flight/useFlightSearch";
import { parseISO } from "date-fns";
import {
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { FlightDetailsDialog } from "./flightDetailsDialog";
import FareDetails from "./fareDetails";

interface FlightDetailsCardProps {
  data: {
    sI: SegmentInfo[];
    totalPriceList: TotalPriceInfo[];
  };
  isReturn?: boolean;
  routeData?: any;
  manualBlockId?: string;
  manualBlockData?: any;
  matchedFlights?: any[];
  isMatched?: boolean;
  matchScore?: number;
  readOnly?: boolean;
  shareId?: string;
}
const FlightDetailsSmallCard = ({
  data,
  isReturn,
  routeData,
  manualBlockId,
  manualBlockData,
  matchedFlights = [],
  isMatched = false,
  matchScore = 0,
  readOnly = false,
  shareId,
}: FlightDetailsCardProps) => {
  const { setSelectedFlights, selectedReturnFlights }: any =
    useFlightBookingStore();

  const { sI, totalPriceList } = data;

  const [selectedFare, setSelectedFare] = useState<any>(
    readOnly ? totalPriceList?.[0] : undefined,
  );
  const [showAll, setShowAll] = useState(false);
  const [show, setShow] = useState(false);
  const [loadingFareId, setLoadingFareId] = useState<string | null>(null);

  const { getFareRules } = useFlightSearch();

  const formatDateAndTime = (dateTimeString: string) => {
    const dateObj = new Date(dateTimeString);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("default", { month: "short" });
    const formattedDate = `${day} ${month}`;
    const hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const formattedTime = `${hours}:${minutes}`;
    return { formattedDate, formattedTime };
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const flight = sI[0];
  const fd = totalPriceList[0]?.fd;

  const displayedFares = showAll ? totalPriceList : totalPriceList.slice(0, 1);
  const headerFares = readOnly
    ? selectedFare
      ? [selectedFare]
      : totalPriceList.slice(0, 1)
    : displayedFares;

  useEffect(() => {
    if (readOnly) return;
    setSelectedFare(
      isReturn
        ? selectedReturnFlights?.return?.fare
        : selectedReturnFlights?.onword?.fare
    );
  }, [selectedReturnFlights, readOnly, isReturn]);

  const handleFareChange = useCallback(
    (fare: any, totalPrice: any) => {
      setSelectedFare(fare);
      setSelectedFlights(isReturn ? "return" : "onward", {
        flight: flight,
        fare: fare,
        totalPrice: totalPrice,
      });
    },
    [setSelectedFare, isReturn, flight]
  );

  const handleFareButtonClick = useCallback(
    (fare: any, totalPrice: any) => {
      setLoadingFareId(fare.id);
      handleFareChange(fare, totalPrice);
      // Reset loading state after a delay (you can adjust this based on your API call)
      // setTimeout(() => {
      //   setLoadingFareId(null);
      // }, 2000);
    },
    [handleFareChange]
  );

  const firstDeparture = sI[0];
  const lastArrival = sI[sI.length - 1];

  const flightDetail = firstDeparture.fD;
  const { da, dt, stops } = firstDeparture;
  const { formattedTime: deparTime, formattedDate: deparDate } =
    formatDateAndTime(dt);
  const { at, aa } = lastArrival;
  const { formattedTime: arriveTime, formattedDate: arriveDate } =
    formatDateAndTime(at);
  const flightImg = `/AirlinesLogo/${flightDetail.aI.code}.png`;
  const firstDepartureTime = firstDeparture.dt
    ? parseISO(firstDeparture.dt)
    : new Date(); // Fallback to current date
  const lastArrivalTime = lastArrival.at
    ? parseISO(lastArrival.at)
    : new Date(); // Fallback to current date

  const totalTravelTimeMinutes = Math.floor(
    (lastArrivalTime.getTime() - firstDepartureTime.getTime()) / (1000 * 60)
  );

  // Helper function to get flight identifier - must match page.tsx logic
  const getFlightId = (flight: any) => {
    // First check if flight already has an ID (set by page.tsx)
    if (flight.id) {
      return flight.id;
    }
    // Try to get ID from totalPriceList first (fare ID)
    if (flight.totalPriceList?.[0]?.id) {
      return flight.totalPriceList[0].id;
    }
    // Fallback to creating ID from segment IDs
    if (flight.sI?.length > 0) {
      return flight.sI.map((segment: any) => segment.id).join("-");
    }
    // Last fallback - use flight number and route
    if (flight.sI?.[0]) {
      const firstSegment = flight.sI[0];
      return `${firstSegment.fD?.fN || "unknown"}-${firstSegment.da?.code || "unknown"
        }-${firstSegment.aa?.code || "unknown"}`;
    }
    return "unknown";
  };

  // Check if this flight is a matched flight from manual block
  const isMatchedFlight = isMatched;
  const matchInfo = matchedFlights.find(
    (match) => match.flight.id === getFlightId(data)
  );

  // Debug logging for manual block mode
  React.useEffect(() => {
    if (manualBlockId) {
      console.log("Flight card data:", {
        flightId: getFlightId(data),
        isMatchedFlight,
        matchInfo,
        matchedFlightsCount: matchedFlights.length,
      });
    }
  }, [
    manualBlockId,
    getFlightId(data),
    isMatchedFlight,
    matchInfo,
    matchedFlights.length,
  ]);

  // Get match data for this flight
  const getMatchData = () => {
    if (!manualBlockId || !matchedFlights.length) return null;
    return matchedFlights.find((match: any) => {
      const idMatches = match.flight.id === getFlightId(data);
      const tripMatches = match.tripType === (isReturn ? "RETURN" : "ONWARD");
      return idMatches && tripMatches;
    });
  };

  const matchData = getMatchData();

  // Auto-select matched flight fare when in manual block mode
  React.useEffect(() => {
    if (manualBlockId && matchData && totalPriceList.length > 0) {
      // Auto-select the first fare of matched flights
      const fareToSelect: any = totalPriceList[0];
      if (fareToSelect) {
        console.log(
          `Auto-selecting fare for matched ${isReturn ? "return" : "onward"
          } flight: ${getFlightId(data)}`
        );
        handleFareChange(fareToSelect, fareToSelect?.fd.ADULT.fC.TF);
      }
    }
  }, [
    manualBlockId,
    matchData,
    totalPriceList,
    isReturn,
    getFlightId(data),
    handleFareChange,
  ]);

  return (
    <div
      className={`rounded-lg p-3 mb-4 shadow-xl relative ${matchData?.isPerfectMatch
        ? "bg-gradient-to-r from-emerald-50 to-green-50 ring-2 ring-emerald-500"
        : matchData?.isExactMatch
          ? "bg-green-50 ring-2 ring-green-500"
          : matchData
            ? "bg-blue-50 ring-1 ring-blue-300"
            : "bg-white"
        }`}
      data-flight-id={getFlightId(data)}
    >
      {/* Match Badge */}
      {matchData && (
        <div className="absolute top-2 right-2 z-1">
          {matchData.isPerfectMatch ? (
            <div className="bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
              ⭐{" "}
              <p className="hidden hover:block duration-300">PERFECT MATCH</p>
            </div>
          ) : matchData.isExactMatch ? (
            <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-md">
              EXACT MATCH
            </div>
          ) : (
            <></>
          )}
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between">
        {/* Flight Details Section */}
        <div className="basis-[65%] lg:basis-[75%] mb-2.5 lg:mb-0">
          <div className="flex flex-col h-full justify-between">
            <div>
              <div className="py-2">
                {sI.length > 1 ? (
                  <div className="flex flex-col md:flex-row md:justify-between items-start gap-2 xl:gap-4">
                    {/* Airline Info */}
                    <div className="flex items-center gap-3 basis-[22%] lg:basis-[18%]">
                      <div className="relative">
                        <Image
                          src={flightImg}
                          alt={flightDetail.aI.name}
                          width={35}
                          height={35}
                          className="rounded-full"
                        />
                        <span className="absolute -bottom-1 -right-1 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
                          {flightDetail.aI.code}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                          {flightDetail.aI.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {flightDetail.fN}
                        </p>
                      </div>
                    </div>

                    {/* Flight Timeline */}
                    <div className="flex items-start justify-around w-[100%] basis-[100%] md:basis-[70%] lg:basis-[76%]">
                      {/* Departure */}
                      <div className="flex-shrink-0 w-auto xl:w-[100px]">
                        <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                          {deparTime}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                          {deparDate}
                        </div>
                        <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                          {/* <div>{da.city}</div> */}
                          <div className="flex items-center gap-1 flex-wrap">
                            <span>({da.code})</span>
                          </div>
                        </div>
                      </div>

                      {/* Flight Path */}
                      <div className="flex flex-col items-center px-2 sm:px-4 mx-2 sm:mx-4 flex-shrink-0 mt-1.5 w-[160px] xl:w-[200px]">
                        <span className="text-[10px] sm:text-xs text-primary mb-1 lg:mb-2 font-semibold">
                          {totalTravelTimeMinutes > 0
                            ? formatDuration(totalTravelTimeMinutes)
                            : "0m"}
                        </span>
                        <FlightLine />
                        <span className="text-[10px] sm:text-xs text-gray-700 mt-2 lg:mt-3 font-bold">
                          {sI[0]?.cT ? `${sI.length - 1} ` : "Non"} - Stop
                        </span>
                      </div>

                      <div className="text-right sm:text-center xl:w-[150px] sm:w-auto">
                        <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                          {arriveTime}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                          {arriveDate}
                        </div>
                        <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                          {/* <div>{aa.city}</div> */}
                          <div className="flex items-center gap-1 justify-end sm:justify-center flex-wrap">
                            <span>({aa.code})</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  sI.map((flight: any, index: number) => {
                    const flightDetails = flight.fD;
                    const { da, dt, at, aa } = flight;
                    const {
                      formattedTime: departureTime,
                      formattedDate: departureDate,
                    } = formatDateAndTime(dt);
                    const {
                      formattedTime: arrivalTime,
                      formattedDate: arrivalDate,
                    } = formatDateAndTime(at);
                    const flightImgs = `/AirlinesLogo/${flightDetails.aI.code}.png`;

                    return (
                      <div
                        key={index}
                        className="flex flex-col md:flex-row md:justify-between items-start gap-2 xl:gap-4"
                      >
                        {/* Airline Info */}
                        <div className="flex items-center gap-3 basis-[22%] lg:basis-[18%]">
                          <div className="relative">
                            <Image
                              src={flightImgs}
                              alt={flightDetails.aI.name}
                              width={40}
                              height={40}
                              className="rounded-full"
                            />
                            <span className="absolute -bottom-1 -right-1 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
                              {flightDetails.aI.code}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                              {flightDetails.aI.name}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {flightDetails.fN}
                            </p>
                          </div>
                        </div>

                        {/* Flight Timeline */}
                        <div className="flex items-start justify-around w-[100%] basis-[100%] md:basis-[70%] lg:basis-[76%]">
                          {/* Departure */}
                          <div className="flex-shrink-0 w-auto xl:w-[100px]">
                            <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                              {departureTime}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                              {departureDate}
                            </div>
                            <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                              {/* <div>{da.city}</div> */}
                              <div className="flex items-center gap-1 flex-wrap">
                                <span>({da.code})</span>
                              </div>
                            </div>
                          </div>

                          {/* Flight Path */}
                          <div className="flex flex-col items-center px-2 sm:px-4 mx-2 sm:mx-4 flex-shrink-0 mt-1.5 w-[160px] xl:w-[200px]">
                            <span className="text-[10px] sm:text-xs text-primary mb-1 lg:mb-2 font-semibold">
                              {formatDuration(flight.duration)}
                            </span>
                            <FlightLine />
                            <span className="text-[10px] sm:text-xs text-gray-700 mt-2 lg:mt-3 font-bold">
                              {flight?.cT ? `${sI.length - 1} ` : "Non"} - Stop
                            </span>
                          </div>

                          {/* Arrival */}
                          {/* flex-shrink-0 */}
                          <div className="text-right sm:text-center xl:w-[150px] sm:w-auto">
                            <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                              {arrivalTime}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                              {arrivalDate}
                            </div>
                            <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                              {/* <div>{aa.city}</div> */}
                              <div className="flex items-center gap-1 justify-end sm:justify-center flex-wrap">
                                <span>({aa.code})</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fare Section */}
        <div className="text-right flex flex-col justify-between items-start gap-1 basis-[25%] pt-3 relative">
          <div className="flex flex-col gap-1">
            {headerFares.map((fare: any, index: number) => {
              const totalPrice = fare.fd.ADULT.fC.TF;

              return (
                <div key={index}>
                  <div
                    className="flex justify-between items-center w-full"
                    key={index}
                  >
                    <div
                      key={index}
                      className="flex flex-col gap-2  rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-start gap-2">
                        {!readOnly && (
                        <input
                          type="radio"
                          id={`fare-${isReturn ? "return" : "onward"}-${index}`}
                          name={`fare-${isReturn ? "return" : "onward"}`}
                          className="mt-1.5 accent-primary"
                          checked={
                            isReturn
                              ? selectedReturnFlights?.return?.fare?.id ===
                              fare.id
                              : selectedReturnFlights?.onword?.fare?.id ===
                              fare.id
                          }
                          onChange={() => {
                            handleFareChange(fare, totalPrice);
                            if (show) {
                              getFareRules({
                                id: fare.id,
                                flowType: "SEARCH",
                              });
                            }
                          }}
                        />
                        )}
                        <label
                          htmlFor={`fare-${isReturn ? "return" : "onward"
                            }-${index}`}
                          className="flex-1"
                        >
                          <p className="font-bold text-sm sm:text-sm text-gray-900">
                            {new Intl.NumberFormat("en-IN", {
                              style: "currency",
                              currency: "INR",
                            }).format(Math.round(totalPrice))}
                          </p>
                          <div className="space-y-1"></div>
                        </label>
                      </div>
                      <Separator />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Button
                      className="text-sm font-bold text-primary bg-white shadow-md border-2 border-primary"
                      onClick={() => setShow(!show)}
                    >
                      {show ? "Hide Details" : "Show Details"}
                    </Button>
                  </div>
                  <p className="flex justify-center">
                    <FlightDetailsDialog sI={sI} />
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {selectedFare && (
        <div className="">
          {show && (
            <div className="py-4">
              <Carousel
                opts={{
                  align: "start",
                  loop: false,
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {totalPriceList.map((fares: any) => (
                    <FareDetails
                      fares={fares}
                      key={fares.id}
                      totalPriceList={totalPriceList}
                      isReturn={isReturn}
                      sI={sI}
                      selectedFare={selectedFare}
                      totalTravelTimeMinutes={totalTravelTimeMinutes}
                      handlerFaresRulesBooks={handleFareButtonClick}
                      loadingFareId={loadingFareId}
                      readOnly={readOnly}
                      shareId={shareId}
                      onSelectFare={readOnly ? setSelectedFare : undefined}
                    />
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-[-20px]" />
                <CarouselNext className="right-[-20px]" />
              </Carousel>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FlightDetailsSmallCard;
